<template>
  <head>
    <meta http-equiv="Content-Type" content="text/html; charset=UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1">
    <title>Discharge Summary</title>
    <link rel="shortcut icon" href="images/favicon.png" type="image/x-icon">
    <link rel="preconnect" href="https://fonts.googleapis.com">
    <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
    <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  </head>

  <body style="padding: 0px; margin: 0px; font-family: 'Montserrat', sans-serif; background-color: #a2a2a21c; box-sizing: border-box;">

  <!-- Loading State -->
  <div v-if="loading" style="display: flex; justify-content: center; align-items: center; height: 100vh;">
    <div style="text-align: center;">
      <div style="border: 4px solid #f3f3f3; border-top: 4px solid #0045a1; border-radius: 50%; width: 50px; height: 50px; animation: spin 1s linear infinite; margin: 0 auto;"></div>
      <p style="margin-top: 20px; color: #666;">Loading discharge summary...</p>
    </div>
  </div>

  <!-- Print Button -->
  <button v-if="!loading && admission" class="print-btn" onclick="window.print()">
    <img src="assets/printer.svg" alt="Print" style="height: 35px;">
  </button>

  <!-- Main Page Content -->
  <page v-if="!loading && admission" class="page" style="width: 210mm; min-height: 297mm; display: flex; position: relative; flex-flow: column; background-color: white;">

    <!-- Main Content Area -->
    <div style="padding: 8.5mm; flex: 1;">

      <!-- Header with Logos -->
      <header style="display: flex; height: auto; min-height: 25mm; align-items: center; position: relative; width: 100%; justify-content: space-between; padding-bottom: 2mm;">
        <div>
          <img :src="state_logo()" style="height: 25mm; margin-right: 0mm;">
        </div>
        <div style="text-align: center; margin-right: 8.5mm; margin-left: 8.5mm;">
          <h2 style="margin-top: 0px; font-size: 18px; text-transform: uppercase; color: #0045a1; font-weight: 700; margin-bottom: 1.2mm; padding-bottom: 0px;">
            {{ facilityName || 'County Referral Hospital' }}
          </h2>
          <h4 style="padding: 0px; margin: 0px; font-size: 10px; font-weight: 400;">
            P.O. Box 00000 | Tel: 000-000-000
          </h4>
          <h3 style="padding: 0px; margin: 8px 0 0 0; font-size: 14px; font-weight: 700; text-transform: uppercase; color: #4ca82c;">
            Patient Discharge Summary
          </h3>
        </div>
        <div>
          <img :src="county_logo()" style="height: 25mm; margin-right: 0mm;">
        </div>
      </header>

      <!-- Blue Line Separator -->
      <div style="margin-top: 2mm;">
        <img src="assets/blue-line.png" style="width: 100%;">
      </div>

      <!-- Patient Information Section -->
      <div style="margin-top: 4mm;">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Patient Information
        </h4>
        <table style="width: 100%; font-size: 11px; margin-top: 2mm;">
          <tr>
            <td style="padding: 2mm; width: 25%;"><strong>Patient Name:</strong></td>
            <td style="padding: 2mm; width: 25%;">{{ admission.patientName }}</td>
            <td style="padding: 2mm; width: 25%;"><strong>Patient ID:</strong></td>
            <td style="padding: 2mm; width: 25%;">{{ admission.patientID }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Gender:</strong></td>
            <td style="padding: 2mm;">{{ admission.patientGender || admission.gender }}</td>
            <td style="padding: 2mm;"><strong>Age:</strong></td>
            <td style="padding: 2mm;">{{ admission.patientAge || admission.age }} years</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>ID Number:</strong></td>
            <td style="padding: 2mm;">{{ admission.patientIdNo || '-' }}</td>
            <td style="padding: 2mm;"><strong>Phone:</strong></td>
            <td style="padding: 2mm;">{{ admission.patientPhone || admission.patientMobile || '-' }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Address:</strong></td>
            <td style="padding: 2mm;" colspan="3">{{ admission.subCountyName || '' }}{{ admission.patientWardName ? ', ' + admission.patientWardName : '' }}</td>
          </tr>
        </table>
      </div>

      <!-- Admission Details Section -->
      <div style="margin-top: 4mm;">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Admission Details
        </h4>
        <table style="width: 100%; font-size: 11px; margin-top: 2mm;">
          <tr>
            <td style="padding: 2mm; width: 25%;"><strong>Admission No:</strong></td>
            <td style="padding: 2mm; width: 25%;">{{ admission.admissionNumber || admission.admissionID }}</td>
            <td style="padding: 2mm; width: 25%;"><strong>Ward:</strong></td>
            <td style="padding: 2mm; width: 25%;">{{ admission.wardName }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Admission Date:</strong></td>
            <td style="padding: 2mm;">{{ formatDateTime(admission.admissionDate) }}</td>
            <td style="padding: 2mm;"><strong>Discharge Date:</strong></td>
            <td style="padding: 2mm;">{{ formatDateTime(admission.dischargeDate) }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Length of Stay:</strong></td>
            <td style="padding: 2mm;">{{ admission.daysAdmitted || 1 }} day(s)</td>
            <td style="padding: 2mm;"><strong>Discharge Type:</strong></td>
            <td style="padding: 2mm;">{{ admission.dischargeType || 'Normal Discharge' }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Attending Doctor:</strong></td>
            <td style="padding: 2mm;">{{ admission.admittingDoctor || admission.doctorName || '-' }}</td>
            <td style="padding: 2mm;"><strong>Bed/Room:</strong></td>
            <td style="padding: 2mm;">{{ admission.bedNumber || '-' }}</td>
          </tr>
        </table>
      </div>

      <!-- Clinical Summary Section -->
      <div style="margin-top: 4mm;">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Clinical Summary
        </h4>
        <p style="padding: 3mm; margin: 0; font-size: 11px; line-height: 1.5; white-space: pre-wrap; min-height: 10mm;">{{ admission.dischargeSummary || admission.clinicalSummary || 'No summary provided.' }}</p>
      </div>

      <!-- Diagnosis Section -->
      <div style="margin-top: 4mm;" v-if="admission.admissionDiagnosis || admission.finalDiagnosis">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Diagnosis
        </h4>
        <table style="width: 100%; font-size: 11px; margin-top: 2mm;">
          <tr v-if="admission.admissionDiagnosis">
            <td style="padding: 2mm; width: 25%;"><strong>Admission Diagnosis:</strong></td>
            <td style="padding: 2mm;">{{ admission.admissionDiagnosis }}</td>
          </tr>
          <tr v-if="admission.finalDiagnosis">
            <td style="padding: 2mm; width: 25%;"><strong>Final Diagnosis:</strong></td>
            <td style="padding: 2mm;">{{ admission.finalDiagnosis }}</td>
          </tr>
        </table>
      </div>

      <!-- Medications During Stay -->
      <div style="margin-top: 4mm;" v-if="medications.length > 0">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Medications During Stay
        </h4>
        <table style="width: 100%; font-size: 10px; margin-top: 2mm; border-collapse: collapse;">
          <thead>
          <tr style="background: #f5f5f5;">
            <th style="padding: 2.5mm; text-align: left; border-bottom: 1px solid #ddd;">#</th>
            <th style="padding: 2.5mm; text-align: left; border-bottom: 1px solid #ddd;">Medication</th>
            <th style="padding: 2.5mm; text-align: left; border-bottom: 1px solid #ddd;">Dosage</th>
            <th style="padding: 2.5mm; text-align: left; border-bottom: 1px solid #ddd;">Frequency</th>
            <th style="padding: 2.5mm; text-align: left; border-bottom: 1px solid #ddd;">Route</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="(med, index) in medications" :key="med.medicationID" style="border-bottom: 1px solid #eee;">
            <td style="padding: 2.5mm;">{{ index + 1 }}</td>
            <td style="padding: 2.5mm;">{{ med.drugName }}</td>
            <td style="padding: 2.5mm;">{{ med.dosage }}</td>
            <td style="padding: 2.5mm;">{{ med.frequency }}</td>
            <td style="padding: 2.5mm;">{{ med.route }}</td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Follow-up Instructions -->
      <div style="margin-top: 4mm;">
        <h4 style="margin: 0; padding: 3mm; background: #e2f2fd; color: #0045a1; font-size: 12px; text-transform: uppercase; border-left: 4px solid #0045a1;">
          Follow-up Instructions
        </h4>
        <table style="width: 100%; font-size: 11px; margin-top: 2mm;">
          <tr>
            <td style="padding: 2mm; width: 25%;"><strong>Follow-up Date:</strong></td>
            <td style="padding: 2mm;">{{ formatDate(admission.followUpDate) || 'As advised' }}</td>
          </tr>
          <tr>
            <td style="padding: 2mm;"><strong>Follow-up Clinic:</strong></td>
            <td style="padding: 2mm;">{{ admission.followUpClinic || 'General Outpatient' }}</td>
          </tr>
        </table>
      </div>

      <!-- Warning Signs -->
      <div style="margin-top: 4mm;">
        <h4 style="margin: 0; padding: 3mm; background: #fff3cd; color: #856404; font-size: 12px; text-transform: uppercase; border-left: 4px solid #ffc107;">
          ⚠ When to Seek Immediate Medical Attention
        </h4>
        <p style="padding: 3mm; margin: 0; font-size: 11px; line-height: 1.5;">
          {{ admission.warningSignals || 'Return immediately if you experience: high fever, severe pain, difficulty breathing, excessive bleeding, worsening symptoms, or any other concerns.' }}
        </p>
      </div>

      <!-- Blue Line Separator -->
      <div style="margin-top: 4mm;">
        <img src="assets/blue-line.png" style="width: 100%;">
      </div>

      <!-- Billing Summary -->
      <div style="margin-top: 4mm; display: flex; justify-content: flex-end;" v-if="billingSummary">
        <table style="font-size: 12px; min-width: 200px;">
          <tr>
            <td style="padding: 2.5mm; border-bottom: 1px solid #ddd;">Total Charges:</td>
            <td style="padding: 2.5mm; text-align: right; border-bottom: 1px solid #ddd;"><strong>KES {{ numberFormat(billingSummary.grandTotal) }}</strong></td>
          </tr>
          <tr>
            <td style="padding: 2.5mm; border-bottom: 1px solid #ddd;">Total Paid:</td>
            <td style="padding: 2.5mm; text-align: right; border-bottom: 1px solid #ddd; color: #28a745;"><strong>KES {{ numberFormat(billingSummary.totalPaid) }}</strong></td>
          </tr>
          <tr>
            <td style="padding: 2.5mm;"><strong>Balance Due:</strong></td>
            <td style="padding: 2.5mm; text-align: right;" :style="{ color: billingSummary.balanceDue > 0 ? '#dc3545' : '#28a745' }">
              <strong>KES {{ numberFormat(billingSummary.balanceDue) }}</strong>
            </td>
          </tr>
        </table>
      </div>

      <!-- Signatures -->
      <div style="margin-top: 8mm; display: flex; justify-content: space-between;">
        <div style="width: 45%;">
          <div style="border-bottom: 1px solid #333; height: 15mm;"></div>
          <p style="margin: 2mm 0 0 0; font-size: 11px;"><strong>Discharged By:</strong></p>
          <p style="margin: 1mm 0; font-size: 11px;">{{ admission.dischargedByName || '-' }}</p>
          <p style="margin: 0; font-size: 10px; color: #666;">Date: {{ formatDateTime(admission.dischargeDate) || formatDateTime(new Date()) }}</p>
        </div>
        <div style="width: 45%;">
          <div style="border-bottom: 1px solid #333; height: 15mm;"></div>
          <p style="margin: 2mm 0 0 0; font-size: 11px;"><strong>Patient/Guardian Signature:</strong></p>
          <p style="margin: 1mm 0; font-size: 10px; color: #666;">Date: _________________</p>
        </div>
      </div>

    </div>

    <!-- Footer -->
    <div style="padding: 5mm 8.5mm; background-color: #e2f2fd; margin-top: auto;">
      <div style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap;">
        <p style="margin: 0; font-size: 10px; color: #333;">
          <strong>Admission No:</strong> {{ admission.admissionNumber || admission.admissionID }}
        </p>
        <p style="margin: 0; font-size: 10px; color: #666; font-style: italic;">
          This is a computer-generated document
        </p>
        <p style="margin: 0; font-size: 10px; color: #333;">
          Printed: {{ getCurrentDateTime() }}
        </p>
      </div>
    </div>

  </page>

  <!-- Not Found State -->
  <div v-if="!loading && !admission" style="display: flex; justify-content: center; align-items: center; height: 100vh;">
    <div style="text-align: center;">
      <div style="font-size: 60px; color: #ffc107;">⚠</div>
      <h3 style="color: #333;">Admission Not Found</h3>
      <p style="color: #666;">The admission record could not be found.</p>
      <button @click="goBack" style="padding: 10px 30px; background: #0045a1; color: white; border: none; border-radius: 5px; cursor: pointer; font-size: 14px;">
        Go Back
      </button>
    </div>
  </div>

  </body>
</template>

<style scoped>
* {
  box-sizing: border-box;
  font-family: 'Montserrat', sans-serif;
  -webkit-print-color-adjust: exact !important;
  color-adjust: exact !important;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.print-btn {
  display: flex;
  z-index: 1000000000;
  position: fixed;
  background: #4ca82c;
  color: white;
  top: 30px;
  right: 30px;
  border-radius: 50%;
  padding: 1rem;
  margin: 0px;
  border: none;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 0px 20px 4px rgb(136 136 136 / 65%);
  cursor: pointer;
  transition: 0.4s;
}

.print-btn:hover {
  background: #3d8a23;
  transform: scale(1.05);
}

.print-btn img {
  height: 35px;
}

.page {
  margin: 10mm auto;
  box-shadow: 0 0 10px rgba(0,0,0,0.1);
}

@media print {
  .print-btn {
    display: none !important;
  }

  body {
    margin: 0px !important;
    padding: 0px !important;
    background-color: white !important;
  }

  .page {
    margin: 0 !important;
    box-shadow: none !important;
  }

  @page {
    size: A4 portrait;
    margin: 0px;
  }
}
</style>

<script>
/* eslint-disable */
import { execute, hospital, county_logo, state_logo } from "@/api";

export default {
  name: "DischargeSummary",
  data() {
    return {
      loading: true,
      admissionID: '',
      facilityName: '',
      admission: null,
      medications: [],
      procedures: [],
      orders: [],
      billingSummary: null,
      latestVitals: null,
      labResults: []
    };
  },
  methods: {
    county_logo() {
      return county_logo;
    },

    state_logo() {
      return state_logo;
    },

    numberFormat(num) {
      return new Intl.NumberFormat("en-GB").format(num || 0);
    },

    formatDate(date) {
      if (!date) return null;
      return new Date(date).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    },

    formatDateTime(date) {
      if (!date) return '-';
      return new Date(date).toLocaleString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getCurrentDateTime() {
      const now = new Date();
      return now.toLocaleString('en-US', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      });
    },

    loadDischargeSummary() {
      const data = new FormData();
      data.append("function", "getDischargeSummary");
      data.append("admissionID", this.admissionID);

      execute(data, hospital)
          .then((res) => {
            this.loading = false;
            if (res.data.success) {
              this.admission = res.data.data.admission;
              this.medications = res.data.data.medications || [];
              this.procedures = res.data.data.procedures || [];
              this.orders = res.data.data.orders || [];
              this.billingSummary = res.data.data.billingSummary || null;
              this.latestVitals = res.data.data.latestVitals || null;
              this.labResults = res.data.data.labResults || [];
            }
          })
          .catch((e) => {
            this.loading = false;
            console.error(e);
          });
    },

    goBack() {
      window.close();
      setTimeout(() => {
        this.$router.go(-1);
      }, 100);
    }
  },
  mounted() {
    // First check route params
    if (this.$route.params.id) {
      this.admissionID = this.$route.params.id;
    }

    // Fallback to localStorage
    if (!this.admissionID) {
      const params = localStorage.getItem('params');
      if (params) {
        try {
          const parsed = JSON.parse(params);
          this.admissionID = parsed.admissionID || '';
        } catch (e) {
          console.error('Error parsing params', e);
        }
      }
    }

    this.facilityName = sessionStorage.getItem("hospitalFacilityName") || 'County Referral Hospital';

    if (this.admissionID) {
      this.loadDischargeSummary();
    } else {
      this.loading = false;
    }
  }
};
</script>